import * as React from 'react';
import { Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  useListContext,
  useRedirect,
  List,
  Datagrid,
  Filter,
  Edit,
  Create,
  Toolbar,
  TopToolbar,
  DeleteWithConfirmButton,
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  SimpleForm,
  SelectInput,
  TextInput,
  Button,
  EditButton,
  ShowButton,
  SaveButton,
  required,
} from 'react-admin';
import { Box } from '@material-ui/core';
import { ArrowBack, Search } from '@material-ui/icons';

const useStyles = makeStyles({
  pathDataField: {
    '& pre': { maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }
  }
});

let amapReady = false;
let areaCalculateFn = null;

const AMAP_KEY = '944be3f3741eeea2caf5a52655b872ad';

function loadAMapApi() {
  return new Promise((resolve) => {
    if (typeof window.AMap !== 'undefined') {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${AMAP_KEY}`;
    script.onload = () => {
      if (window.AMap && window.AMap.GeometryUtil) {
        areaCalculateFn = window.AMap.GeometryUtil.calculateArea;
      }
      resolve();
    };
    script.onerror = resolve;
    document.head.appendChild(script);
  });
}

const AreaField = (props) => {
  const record = props.record;
  const [area, setArea] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!record || !record.PathData) {
      setLoading(false);
      return;
    }

    let path;
    try {
      path = JSON.parse(record.PathData);
    } catch {
      setLoading(false);
      return;
    }

    if (!Array.isArray(path) || path.length < 3) {
      setLoading(false);
      return;
    }

    const calculate = async () => {
      await loadAMapApi();

      if (window.AMap && window.AMap.GeometryUtil) {
        try {
          const lngLats = path.map(p => new window.AMap.LngLat(p[0], p[1]));
          const result = window.AMap.GeometryUtil.calculateArea(lngLats);
          setArea(result);
        } catch {
          const result = calculateAreaFallback(path);
          setArea(result);
        }
      } else {
        const result = calculateAreaFallback(path);
        setArea(result);
      }
      setLoading(false);
    };

    calculate();
  }, [record]);

  if (loading) return <span>计算中...</span>;
  if (area === null) return <span>-</span>;

  if (area >= 1000000) {
    return <span>{(area / 1000000).toFixed(2)} km²</span>;
  }
  return <span>{Math.round(area)} m²</span>;
};

function calculateAreaFallback(path) {
  let area = 0;
  const n = path.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += path[i][0] * path[j][1];
    area -= path[j][0] * path[i][1];
  }
  area = Math.abs(area) / 2;
  const avgLat = path.reduce((sum, p) => sum + p[1], 0) / n;
  const latRad = avgLat * Math.PI / 180;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  const metersPerDegLat = 110540;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = (path[j][0] - path[i][0]) * metersPerDegLng;
    const dy = (path[j][1] - path[i][1]) * metersPerDegLat;
    sum += dx * dy;
  }
  area = Math.abs(sum) / 2;
  return area;
}

const useTxtData = {
  page: { tableName: '地图标注' },
  table: {
    id: 'ID',
    name: '名称',
    category: '分类',
    shapeType: '形状类型',
    pathData: '路径数据',
    createTime: '创建时间',
  }
};

const CATEGORY_CHOICES = [
  { id: 'marker', name: '标注点' },
  { id: 'polygon', name: '多边形' },
  { id: 'polyline', name: '折线' },
  { id: 'circle', name: '圆形' },
  { id: 'rectangle', name: '矩形' },
];

const SHAPE_TYPE_CHOICES = [
  { id: 'Point', name: '点' },
  { id: 'Polygon', name: '多边形' },
  { id: 'LineString', name: '折线' },
  { id: 'Circle', name: '圆形' },
  { id: 'Rectangle', name: '矩形' },
];

const Filters = (props) => {
  const { displayedFilters, filterValues, setFilters } = useListContext();
  const onSubmit = values => {
    if (Object.keys(values).length > 0) { setFilters(values); }
    else { setFilters({}, []); }
  };
  return (
    <Filter {...props} onSubmit={onSubmit}>
      <TextInput label="名称" source="Name" alwaysOn resettable />
      <SelectInput label="分类" source="Category" choices={CATEGORY_CHOICES} alwaysOn resettable />
    </Filter>
  );
};

const RowAction = (props) => {
  return (
    <div style={{ textAlign: 'right' }}>
      <EditButton {...props} /><br />
      <ShowButton {...props} /><br />
      <DeleteWithConfirmButton {...props} confirmTitle="删除确认" confirmContent="确认要删除该标注吗？" />
    </div>
  );
};

export const MapAnnotationList = (props) => {
  const classes = useStyles();
  return (
    <List {...props} title={useTxtData.page.tableName + '列表'} sort={{ field: 'CreateTime', order: 'DESC' }} filters={<Filters />}>
      <Datagrid>
        <TextField label={useTxtData.table.id} source="Id" />
        <TextField label={useTxtData.table.name} source="Name" />
        <TextField label={useTxtData.table.category} source="Category" />
        <TextField label={useTxtData.table.shapeType} source="ShapeType" />
        <AreaField label="面积" />
        <DateField label={useTxtData.table.createTime} source="CreateTime" showTime />
        <RowAction />
      </Datagrid>
    </List>
  );
};

export const MapAnnotationShow = (props) => {
  const ShowActions = (props) => {
    const { basePath } = props;
    const redirect = useRedirect();
    return (
      <TopToolbar>
        <Button label="返回列表" onClick={() => redirect(basePath)} variant="text">
          <ArrowBack />
        </Button>
        <EditButton basePath={basePath} record={props.data} />
      </TopToolbar>
    );
  };

  const classes = useStyles();
  return (
    <Show title={'查看' + useTxtData.page.tableName} {...props} actions={<ShowActions />}>
      <SimpleShowLayout>
        <TextField label={useTxtData.table.id} source="Id" />
        <TextField label={useTxtData.table.name} source="Name" />
        <TextField label={useTxtData.table.category} source="Category" />
        <TextField label={useTxtData.table.shapeType} source="ShapeType" />
        <TextField label={useTxtData.table.pathData} source="PathData" className={classes.pathDataField} />
        <AreaField label="面积" />
        <DateField label={useTxtData.table.createTime} source="CreateTime" showTime />
      </SimpleShowLayout>
    </Show>
  );
};

export const MapAnnotationEdit = (props) => {
  const PostEditActions = (props) => {
    const { basePath } = props;
    const redirect = useRedirect();
    return (
      <TopToolbar {...props}>
        <Button label="返回列表" onClick={() => redirect(basePath)} variant="text">
          <ArrowBack />
        </Button>
      </TopToolbar>
    );
  };
  const PostEditToolbar = (props) => {
    return (
      <Toolbar {...props}>
        <SaveButton />
      </Toolbar>
    );
  };

  return (
    <Edit title={'编辑' + useTxtData.page.tableName} undoable={false} {...props} actions={<PostEditActions />}>
      <SimpleForm toolbar={<PostEditToolbar />}>
        <TextField label={useTxtData.table.id} source="Id" disabled />
        <TextInput label={useTxtData.table.name} source="Name" validate={[required()]} />
        <SelectInput label={useTxtData.table.category} source="Category" choices={CATEGORY_CHOICES} />
        <SelectInput label={useTxtData.table.shapeType} source="ShapeType" choices={SHAPE_TYPE_CHOICES} />
        <TextInput label={useTxtData.table.pathData} source="PathData" multiline />
        <DateField label={useTxtData.table.createTime} source="CreateTime" showTime disabled />
      </SimpleForm>
    </Edit>
  );
};

export const MapAnnotationCreate = (props) => {
  const PostCreateActions = (props) => {
    const { basePath } = props;
    const redirect = useRedirect();
    return (
      <TopToolbar {...props}>
        <Button label="返回列表" onClick={() => redirect(basePath)} variant="text">
          <ArrowBack />
        </Button>
      </TopToolbar>
    );
  };
  const PostCreateToolbar = (props) => {
    const { basePath } = props;
    return (
      <Toolbar {...props}>
        <SaveButton label="保存&列表" redirect={basePath} submitOnEnter={true} />
        <SaveButton label="保存&继续" redirect={false} submitOnEnter={false} variant="text" />
      </Toolbar>
    );
  };

  return (
    <Create title={'新增' + useTxtData.page.tableName} undoable={false} {...props} actions={<PostCreateActions />}>
      <SimpleForm toolbar={<PostCreateToolbar />}>
        <TextInput label={useTxtData.table.name} source="Name" validate={[required()]} />
        <SelectInput label={useTxtData.table.category} source="Category" choices={CATEGORY_CHOICES} />
        <SelectInput label={useTxtData.table.shapeType} source="ShapeType" choices={SHAPE_TYPE_CHOICES} />
        <TextInput label={useTxtData.table.pathData} source="PathData" multiline />
      </SimpleForm>
    </Create>
  );
};